/**
 * Cost cell for the usage summaries (spec section 24 "Display unknown cost as unknown rather than
 * zero"). Three cases:
 * - no usage in the window: `--` labelled "No usage" (a $0 estimate over zero requests is not a cost);
 * - unknown estimate: `--` with the qualifier "unknown", never $0;
 * - otherwise the estimate, labelled Reconciled only once every record is reconciled.
 */
export interface UsageCostSummary {
  requestCount: number;
  estimatedCostMicroUsd: number | null | undefined;
  reconciledCount: number;
  estimatedCount: number;
  unknownCount: number;
}

export interface UsageCostCell {
  label: string;
  /** Formatted value or `--`. */
  value: string;
  tone: 'ok' | 'warn' | 'mute';
  unit?: string;
}

export function usageCostCell(summary: UsageCostSummary, options: { hasUsage?: boolean; format: (microUsd: number) => string }): UsageCostCell {
  const hasUsage = options.hasUsage ?? summary.requestCount > 0;
  if (!hasUsage) return { label: 'No usage', value: '--', tone: 'mute' };
  const reconciled = summary.reconciledCount > 0 && summary.estimatedCount === 0 && summary.unknownCount === 0;
  const label = reconciled ? 'Reconciled cost' : 'Estimated cost';
  const est = summary.estimatedCostMicroUsd;
  if (est === null || est === undefined || (summary.unknownCount > 0 && summary.estimatedCount + summary.reconciledCount === 0)) return { label, value: '--', tone: 'mute', unit: 'unknown' };
  return { label, value: options.format(est), tone: reconciled ? 'ok' : 'warn', ...(summary.unknownCount > 0 ? { unit: `+${summary.unknownCount} unknown` } : {}) };
}
